import React from "react";
import {
    FormLayout,
    HeaderActions,
    Heading,
    Input,
    LoadingProgress,
    Panel,
    Switch,
    Text,
} from "@/ui";
import { Icon } from "@/components";
import { useFormActions, useForm, useMeta, useValidator } from "@/hooks";
import {
    MUTATION_UPDATE_STRIPE_APP,
    QUERY_LIST_STORE_APPS,
} from "../../apollo/module-operations/app";
import _ from "lodash";
import styles from "./GeneralsForm.module.scss";

const SaveIcon = () => <Icon icon="save" />;

const fieldError = (validator, name) =>
    validator?.[name]?.isInvalid ? validator[name].message : undefined;

const StripeForm = ({ app }) => {
    const { user } = useMeta();
    const { inputs, setInputs, inputChange } = useForm();

    if (!_.get(inputs, "id", null)) {
        setInputs(app);
    }

    const options = {
        variables: {
            account_id: user.accountId,
            store_id: user.storeId,
            input: {
                ...inputs,
                live_mode: inputs.live_mode ? true : false,
                stripe_payment_category: "PAYMENT_TOKEN_FRONTEND",
                type: "Stripe",
            },
        },
    };

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "public_test_key",
                method: "isEmpty",
                validWhen: false,
                message: "This Public Key is required.",
            },
            {
                field: "public_key",
                method: "isEmpty",
                validWhen: false,
                message: "This Public Key is required.",
            },
            {
                field: "secret_test_key",
                method: "isEmpty",
                validWhen: false,
                message: "This Secret Key is required.",
            },
            {
                field: "secret_key",
                method: "isEmpty",
                validWhen: false,
                message: "This Secret Key is required.",
            },
        ],
        {
            ...inputs,
        },
    );

    const { createOrUpdate, loading } = useFormActions(
        "id",
        "Stripe",
        "",
        MUTATION_UPDATE_STRIPE_APP,
        [
            {
                query: QUERY_LIST_STORE_APPS,
                variables: {
                    account_id: user.accountId,
                    store_id: user.storeId,
                },
                skip: user.loading || !user.accountId || !user.storeId,
            },
        ],
        options,
        "",
        isValid,
        isSubmited,
        "secondary",
    );

    const publicKeyField = inputs.live_mode ? "public_test_key" : "public_key";
    const secretKeyField = inputs.live_mode ? "secret_test_key" : "secret_key";

    return (
        <Panel
            className={styles.root}
            header={
                <div className={styles.headerBar}>
                    <div className={styles.headerText}>
                        <Heading level={4}>Stripe</Heading>
                        <Text tone="secondary" variant="body-sm">
                            Stripe Information
                        </Text>
                    </div>
                    <HeaderActions
                        actions={[
                            {
                                id: "update",
                                label: "Update",
                                icon: loading ? undefined : <SaveIcon />,
                                loading,
                                disabled: loading,
                                onClick: () => createOrUpdate(),
                            },
                        ]}
                        aria-label="Stripe actions"
                    />
                </div>
            }
            showHeaderDivider
        >
            {user.loading ? (
                <div className={styles.loading}>
                    <LoadingProgress
                        indeterminate
                        indicator="circular"
                        size="sm"
                        title="Loading"
                    />
                </div>
            ) : (
                <FormLayout>
                    <FormLayout.Section title="Information">
                        <Switch
                            checked={Boolean(inputs.live_mode)}
                            label="Test Mode"
                            name="live_mode"
                            onChange={inputChange}
                        />
                        <FormLayout.Grid columns={2} maxWidth="twoThirds">
                            <Input
                                error={fieldError(validator, publicKeyField)}
                                fullWidth
                                label="Public Key"
                                name={publicKeyField}
                                onChange={inputChange}
                                value={inputs[publicKeyField] || ""}
                            />
                        </FormLayout.Grid>
                        <FormLayout.Grid columns={2} maxWidth="twoThirds">
                            <Input
                                error={fieldError(validator, secretKeyField)}
                                fullWidth
                                label="Secret Key"
                                name={secretKeyField}
                                onChange={inputChange}
                                value={inputs[secretKeyField] || ""}
                            />
                        </FormLayout.Grid>
                    </FormLayout.Section>
                </FormLayout>
            )}
        </Panel>
    );
};

export default StripeForm;
